import React, { useCallback, useEffect, useState } from 'react';
import {
  AppetiteCarrier,
  AppetiteOutcome,
  AppetiteSentiment,
  getAppetiteCarriers,
  saveAppetiteFeedback,
} from '../../services/appetiteApi';

// Per-carrier feedback loop: like/dislike, free-text note, and the real-world
// outcome (Accepted/Declined). Each change is persisted and re-aggregated into
// the appetite graph so future recommendations learn from results.

interface AppetiteFeedbackPanelProps {
  quoteId: string;
}

function trackRecord(c: AppetiteCarrier): string | null {
  if (!c.outcomeSampleSize) return null;
  const rate = c.learnedAcceptanceRate != null ? `${Math.round(c.learnedAcceptanceRate * 100)}% accepted` : 'no rate';
  return `${rate} · ${c.outcomeSampleSize} outcome${c.outcomeSampleSize === 1 ? '' : 's'}`;
}

const AppetiteFeedbackPanel: React.FC<AppetiteFeedbackPanelProps> = ({ quoteId }) => {
  const [carriers, setCarriers] = useState<AppetiteCarrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingCarrier, setSavingCarrier] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getAppetiteCarriers(quoteId);
      setCarriers(list);
      setDrafts(Object.fromEntries(list.map((c) => [c.carrier, c.feedbackText ?? ''])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load carriers.');
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => { load(); }, [load]);

  // Persist the full current feedback state for one carrier (optimistic).
  const persist = useCallback(async (carrier: AppetiteCarrier, patch: Partial<AppetiteCarrier>) => {
    const next: AppetiteCarrier = { ...carrier, ...patch };
    setCarriers((prev) => prev.map((c) => (c.carrier === carrier.carrier ? next : c)));
    setSavingCarrier(carrier.carrier);
    try {
      await saveAppetiteFeedback({
        quoteId,
        carrier: carrier.carrier,
        sentiment: next.sentiment ?? null,
        feedbackText: next.feedbackText ?? null,
        outcome: next.outcome ?? null,
        recommendationScore: carrier.score,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save feedback.');
      setCarriers((prev) => prev.map((c) => (c.carrier === carrier.carrier ? carrier : c))); // revert
    } finally {
      setSavingCarrier(null);
    }
  }, [quoteId]);

  const toggleSentiment = (c: AppetiteCarrier, value: AppetiteSentiment) =>
    persist(c, { sentiment: c.sentiment === value ? null : value });

  const setOutcome = (c: AppetiteCarrier, value: string) =>
    persist(c, { outcome: (value || null) as AppetiteOutcome | null });

  const saveNote = (c: AppetiteCarrier) =>
    persist(c, { feedbackText: (drafts[c.carrier] ?? '').trim() || null });

  if (loading) return <div className="qd-fb-state">Loading carriers…</div>;
  if (error && carriers.length === 0) return <div className="qd-fb-state qd-fb-state--err">⚠️ {error}</div>;
  if (carriers.length === 0) return <div className="qd-fb-state">No ranked carriers for this prospect yet.</div>;

  return (
    <div className="qd-fb">
      <div className="qd-fb-intro">
        Rate each recommendation and record the carrier's decision. Your feedback trains
        future recommendations.
      </div>
      {error && <div className="qd-fb-state qd-fb-state--err">⚠️ {error}</div>}

      {carriers.map((c, i) => {
        const record = trackRecord(c);
        const isOpen = !!expanded[c.carrier];
        return (
          <div className="qd-fb-item" key={c.carrier}>
            <div className="qd-fb-head">
              <span className="qd-fb-rank">#{i + 1}</span>
              <div className="qd-fb-name">
                <div className="qd-fb-carrier">{c.carrier}</div>
                <div className="qd-fb-meta">
                  {[c.marketType, c.confidence && `${c.confidence} confidence`, `fit ${c.score}`]
                    .filter(Boolean)
                    .join(' · ')}
                  {record && <span className="qd-fb-record"> · {record}</span>}
                </div>
              </div>
              <div className="qd-fb-vote">
                <button
                  className={`qd-fb-vbtn${c.sentiment === 'LIKE' ? ' qd-fb-vbtn--like' : ''}`}
                  title="Good recommendation"
                  aria-label="Like"
                  disabled={savingCarrier === c.carrier}
                  onClick={() => toggleSentiment(c, 'LIKE')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                  </svg>
                </button>
                <button
                  className={`qd-fb-vbtn${c.sentiment === 'DISLIKE' ? ' qd-fb-vbtn--dislike' : ''}`}
                  title="Poor recommendation"
                  aria-label="Dislike"
                  disabled={savingCarrier === c.carrier}
                  onClick={() => toggleSentiment(c, 'DISLIKE')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="qd-fb-controls">
              <label className="qd-fb-outcome">
                Outcome
                <select
                  value={c.outcome ?? ''}
                  disabled={savingCarrier === c.carrier}
                  onChange={(e) => setOutcome(c, e.target.value)}
                >
                  <option value="">Pending</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="DECLINED">Declined</option>
                </select>
              </label>
              <button
                className="qd-fb-notebtn"
                onClick={() => setExpanded((p) => ({ ...p, [c.carrier]: !isOpen }))}
              >
                {c.feedbackText ? 'Edit note' : 'Add note'}
              </button>
            </div>

            {isOpen && (
              <div className="qd-fb-note">
                <textarea
                  className="qd-fb-textarea"
                  placeholder="Why did this carrier work — or not? (underwriting, pricing, appetite…)"
                  value={drafts[c.carrier] ?? ''}
                  onChange={(e) => setDrafts((p) => ({ ...p, [c.carrier]: e.target.value }))}
                  rows={2}
                />
                <button
                  className="qd-fb-save"
                  disabled={savingCarrier === c.carrier || (drafts[c.carrier] ?? '') === (c.feedbackText ?? '')}
                  onClick={() => saveNote(c)}
                >
                  Save note
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AppetiteFeedbackPanel;
